/**
 * Minimal, dependency-free color math for the design tokens.
 *
 * The look-and-feel guide derives every scale in OKLCH (perceptual lightness
 * and chroma with a stable hue), so brand-tinted neutrals and hover/active
 * steps stay consistent whatever the tenant primary is. Formulas are the
 * public OKLab reference (Björn Ottosson) with a chroma-reducing gamut clamp.
 */

export interface Oklch {
  /** Lightness 0..1 */
  l: number;
  /** Chroma, ~0..0.37 for sRGB */
  c: number;
  /** Hue in degrees 0..360 */
  h: number;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const hexToRgb = (hex: string): Rgb => {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((char) => char + char)
          .join('')
      : clean;
  return {
    r: parseInt(full.slice(0, 2), 16) / 255,
    g: parseInt(full.slice(2, 4), 16) / 255,
    b: parseInt(full.slice(4, 6), 16) / 255,
  };
};

export const rgbToHex = ({ r, g, b }: Rgb): string => {
  const toByte = (channel: number): string =>
    Math.round(clamp01(channel) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`.toUpperCase();
};

const srgbToLinear = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

const linearToSrgb = (channel: number): number =>
  channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055;

const linearRgbToOklch = ({ r, g, b }: Rgb): Oklch => {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const c = Math.sqrt(a * a + bb * bb);
  const h = ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
};

const oklchToLinearRgb = ({ l: L, c, h }: Oklch): Rgb => {
  const a = c * Math.cos((h * Math.PI) / 180);
  const bb = c * Math.sin((h * Math.PI) / 180);

  const l = (L + 0.3963377774 * a + 0.2158037573 * bb) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * bb) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * bb) ** 3;

  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
};

const inGamut = ({ r, g, b }: Rgb): boolean =>
  [r, g, b].every((channel) => channel >= -0.0005 && channel <= 1.0005);

export const toOklch = (hex: string): Oklch => {
  const { r, g, b } = hexToRgb(hex);
  return linearRgbToOklch({
    r: srgbToLinear(r),
    g: srgbToLinear(g),
    b: srgbToLinear(b),
  });
};

/**
 * OKLCH → hex. Colors outside sRGB keep lightness and hue and lose chroma
 * until they fit, which is what keeps a tint or a shade "the same color".
 */
export const fromOklch = (color: Oklch): string => {
  let { c } = color;
  let linear = oklchToLinearRgb({ ...color, c });
  let low = 0;
  let high = c;

  if (!inGamut(linear)) {
    for (let i = 0; i < 20; i += 1) {
      c = (low + high) / 2;
      linear = oklchToLinearRgb({ ...color, c });
      if (inGamut(linear)) {
        low = c;
      } else {
        high = c;
      }
    }
    linear = oklchToLinearRgb({ ...color, c: low });
  }

  return rgbToHex({
    r: linearToSrgb(clamp01(linear.r)),
    g: linearToSrgb(clamp01(linear.g)),
    b: linearToSrgb(clamp01(linear.b)),
  });
};

export const withLightness = (hex: string, l: number): string =>
  fromOklch({ ...toOklch(hex), l: clamp01(l) });

export const shiftLightness = (hex: string, delta: number): string =>
  fromOklch({ ...toOklch(hex), l: clamp01(toOklch(hex).l + delta) });

export const withChroma = (hex: string, c: number): string =>
  fromOklch({ ...toOklch(hex), c: Math.max(0, c) });

export const scaleChroma = (hex: string, factor: number): string =>
  fromOklch({ ...toOklch(hex), c: Math.max(0, toOklch(hex).c * factor) });

/** WCAG relative luminance of an sRGB hex. */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
};

/** WCAG contrast ratio (1..21). AA text needs 4.5, large text and UI 3. */
export const contrastRatio = (
  foreground: string,
  background: string
): number => {
  const l1 = relativeLuminance(foreground);
  const l2 = relativeLuminance(background);
  const [light, dark] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (light + 0.05) / (dark + 0.05);
};

/** `rgb(r g b / alpha)` string for shadows and overlays derived from a hex. */
export const rgbAlpha = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(
    b * 255
  )} / ${alpha})`;
};
